export type BrowserAudioSessionType = "auto" | "playback" | "play-and-record";

type BrowserAudioSession = {
  type: BrowserAudioSessionType;
};

type StreamRequester = () => Promise<MediaStream>;
type AudioSessionSetter = (type: BrowserAudioSessionType) => void;

export interface RetainedMicrophoneStreamController {
  getStream: () => Promise<MediaStream>;
  pauseStream: (stream: MediaStream) => Promise<void>;
  resumeStream: (stream: MediaStream) => Promise<MediaStream>;
  stop: () => void;
}

export function setBrowserAudioSessionType(type: BrowserAudioSessionType): void {
  const audioSession = (navigator as Navigator & { audioSession?: BrowserAudioSession }).audioSession;
  if (!audioSession) return;
  try {
    audioSession.type = type;
  } catch {
    // Older WebKit builds can expose a read-only draft implementation.
  }
}

function hasLiveAudioTrack(stream: MediaStream | null): stream is MediaStream {
  return stream !== null && stream.getAudioTracks().some(track => track.readyState === "live");
}

export function createRetainedMicrophoneStreamController(
  requestStream: StreamRequester = () => navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      autoGainControl: true,
      noiseSuppression: true,
    },
  }),
  setAudioSessionType: AudioSessionSetter = setBrowserAudioSessionType,
): RetainedMicrophoneStreamController {
  let retainedStream: MediaStream | null = null;

  const getStream = async (): Promise<MediaStream> => {
    if (hasLiveAudioTrack(retainedStream)) return retainedStream;
    setAudioSessionType("play-and-record");
    try {
      retainedStream = await requestStream();
      return retainedStream;
    } finally {
      // Keep video playback on its established route and volume after the
      // one-time microphone acquisition. Transient VAD pauses do not change
      // this session again.
      setAudioSessionType("playback");
    }
  };

  return {
    getStream,
    pauseStream: async stream => {
      // MicVAD.pause() still disconnects its processing node and pauses its
      // frame processor. Keeping the track live prevents an iOS audio-route
      // teardown between transcription, reply playback, and the next turn.
      if (hasLiveAudioTrack(stream)) retainedStream = stream;
    },
    resumeStream: async stream => {
      if (hasLiveAudioTrack(stream)) {
        retainedStream = stream;
        return stream;
      }
      if (hasLiveAudioTrack(retainedStream)) return retainedStream;
      return getStream();
    },
    stop: () => {
      retainedStream?.getTracks().forEach(track => track.stop());
      retainedStream = null;
      setAudioSessionType("playback");
    },
  };
}
